import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_15_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-15-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-15',
  title: 'Batería solar de 15 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 15 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 15 kWh precio',
    'acumulador solar 15 kWh coste',
    'batería fotovoltaica 15 kWh',
    'cuánto cuesta una batería de 15 kWh',
  ],
  eyebrow: 'Tamaño de batería: 15 kWh',
  h1: 'Batería solar de 15 kWh: precio y valores orientativos',
  intro:
    'Una batería de 15 kWh merece la pena sobre todo para hogares con bomba de calor o carga ' +
    `habitual de un coche eléctrico, combinada con una instalación de referencia de ` +
    `${STORAGE_REFERENCE_KWP} kWp. Aquí encontrará valores orientativos sobre el tamaño de hogar ` +
    'adecuado, el autoconsumo con batería y justo debajo una calculadora con el precio medio ya ' +
    'calculado para esta combinación a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 15 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 15 kWh?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 15 kWh encaja con hogares con bomba de calor o con carga habitual de ' +
        'un coche eléctrico. Con ambos grandes consumidores a la vez, puede merecer la pena el ' +
        'tamaño de 20 kWh.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Con batería solar, este ` +
        `porcentaje sube en el modelo de cálculo unos ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `puntos hasta rondar el ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - la bomba de calor y el ` +
        'coche eléctrico aportan cada uno una bonificación adicional en el modelo, hasta un límite ' +
        'del 85 %. Un cálculo exacto según su consumo lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Con 15 kWh, una caja de respaldo para suministro de emergencia suele alcanzar también ' +
        'para partes más grandes del hogar durante un periodo más largo - el tiempo exacto depende ' +
        'del consumo real de los aparatos conectados. Si un presupuesto incluye o no esta función ' +
        'depende de cada instalador - conviene preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        'instalación - la investigación de mercado de 2026 muestra un rango de entre ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh, con notable impacto en el presupuesto total para 15 kWh. En Alemania existen ' +
        'ayudas específicas para baterías según la región (p. ej. el programa del estado federado ' +
        'de Berlín, con subvención para baterías); en Austria, el sistema EAG añade 150 €/kWh para ' +
        'el acumulador - un cálculo exacto para su combinación lo ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Necesito 15 kWh si tengo bomba de calor?',
      a: 'Una bomba de calor aumenta el posible autoconsumo en el modelo de cálculo - 15 kWh es un tamaño habitual para esa combinación. El tamaño exacto depende del consumo real de calefacción, ver la calculadora de dimensionamiento.',
    },
    {
      q: '¿Es 15 kWh excesivo para hogares sin bomba de calor ni coche eléctrico?',
      a: 'En muchos casos sí - sin estos grandes consumidores, 10 o 12 kWh suele bastar para cubrir gran parte del autoconsumo posible.',
    },
    {
      q: '¿Con qué tamaño de instalación se combina normalmente una batería de 15 kWh?',
      a: `Aquí se toma como referencia una instalación de ${STORAGE_REFERENCE_KWP} kWp; en la práctica también se combina a menudo con 15 kWp. La calculadora ofrece una recomendación exacta para su tejado.`,
    },
    {
      q: '¿Es suficiente 15 kWh para ser autosuficiente durante la noche?',
      a: 'Sí, esto suele bastar incluso con un consumo por encima de la media, por ejemplo con carga de coche eléctrico o bomba de calor. Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 12 kWh: precio y valores orientativos', href: '/bateria-solar-12-kwh-precio' },
    { label: 'Batería solar de 20 kWh: precio y valores orientativos', href: '/bateria-solar-20-kwh-precio' },
    { label: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-15-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
